/** Admin · identity-verification provider config (GET/PUT /admin/providers, owner-only PUT). OFF by default → demo check. */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IdType } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { USD_PER_INR } from "../../lib/format.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Info, Alert, ShieldCheck } from "../../components/icons.js";
import { Switch, apiErrorText } from "./adminUi.js";

type Provider = "sim" | "hyperverge" | "idfy" | "iproov";
type Method = "id_document" | "otp";

/**
 * Cents-precision USD for the tiny per-check costs shown here. The shared usd() helper rounds to
 * whole dollars, which collapses sub-₹86 figures to $0/$1 — useless for a ~₹15–90 range — so we
 * format these inline off the single shared rate (USD_PER_INR) to keep the dual-currency truth
 * without a second rate constant. */
const usdc = (inr: number): string => `$${(inr * USD_PER_INR).toFixed(2)}`;

interface IdVerificationView {
  enabled: boolean;
  provider: Provider;
  method: Method;
  allowedIdTypes: IdType[];
  apiKeyMasked: string | null;
  hasKey: boolean;
}

const METHODS: { value: Method; label: string; desc: string }[] = [
  {
    value: "id_document",
    label: "Government ID + liveness",
    desc: "Candidate picks a government ID and completes a face-match/liveness check via the provider below.",
  },
  {
    value: "otp",
    label: "Email OTP",
    desc: "Candidate verifies with a 6-digit code emailed to their invite address — no document, no provider needed. Useful while a document provider isn't wired up yet.",
  },
];

interface ProvidersResponse {
  idVerification: IdVerificationView;
}

const PROVIDERS: { value: Provider; label: string; cost: string }[] = [
  { value: "sim", label: "Simulated (demo)", cost: `₹0 (${usdc(0)}) · demo check — always passes, labeled to the candidate` },
  {
    value: "hyperverge",
    label: "HyperVerge",
    cost: `~₹15–35 (${usdc(15)}–${usdc(35)}) per check · fits inside the ₹66 (${usdc(66)}) liveness COGS at the low end`,
  },
  {
    value: "idfy",
    label: "IDfy",
    cost: `~₹15–35 (${usdc(15)}–${usdc(35)}) per check · fits inside the ₹66 (${usdc(66)}) liveness COGS at the low end`,
  },
  {
    value: "iproov",
    label: "iProov",
    cost: `~₹40–90 (${usdc(40)}–${usdc(90)}) per check · higher-assurance liveness, above the COGS line`,
  },
];

const ID_TYPES: { value: IdType; label: string }[] = [
  { value: "aadhaar_digilocker", label: "DigiLocker / Aadhaar" },
  { value: "pan", label: "PAN" },
  { value: "driving_license", label: "Driving licence" },
  { value: "passport", label: "Passport" },
  { value: "voter_id", label: "Voter ID" },
];

const providerLabel = (p: Provider): string => PROVIDERS.find((x) => x.value === p)?.label ?? p;

export function Providers(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-providers"],
    queryFn: () => api.get<ProvidersResponse>("/admin/providers"),
  });

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<Provider>("sim");
  const [method, setMethod] = useState<Method>("id_document");
  const [allowedIdTypes, setAllowedIdTypes] = useState<IdType[]>([]);
  const [apiKey, setApiKey] = useState("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (data && !hydrated) {
      setEnabled(data.idVerification.enabled);
      setProvider(data.idVerification.provider);
      setMethod(data.idVerification.method);
      setAllowedIdTypes(data.idVerification.allowedIdTypes);
      setHydrated(true);
    }
  }, [data, hydrated]);

  const save = useMutation({
    mutationFn: () =>
      api.put<ProvidersResponse>("/admin/providers/id-verification", {
        enabled,
        provider,
        method,
        allowedIdTypes,
        apiKey: apiKey.trim() || undefined,
      }),
    onSuccess: () => {
      setApiKey("");
      void queryClient.invalidateQueries({ queryKey: ["admin-providers"] });
    },
  });

  const toggleIdType = (t: IdType): void =>
    setAllowedIdTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const activeCost = PROVIDERS.find((p) => p.value === provider)?.cost ?? "";
  const realProvider = provider !== "sim";

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Identity verification</h1>
          <div className="sub">Government-ID + liveness at candidate check-in · OFF by default</div>
        </div>
        <span className="pill n">Owner</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading provider config…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load provider config. Please retry in a moment.</span>
        </div>
      )}

      {data && (
        <>
          {!data.idVerification.enabled && (
            <div className="banner warn" style={{ marginBottom: 16 }}>
              <Alert />
              <div>
                <b>OFF = demo check.</b> With verification disabled, check-in runs a <b>simulated</b> check that
                always passes and is <b>labeled to the candidate</b> as a demo. Turn it on once a vendor contract is
                signed — no real ID check runs until then.
              </div>
            </div>
          )}

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>Current configuration</h3>
                <span className={data.idVerification.enabled ? "pill v" : "pill n"}>
                  {data.idVerification.enabled ? "Live" : "Demo (off)"}
                </span>
              </div>
              <div className="card-b">
                <div className="krow">
                  <span className="k">Status</span>
                  <span className="v">
                    {data.idVerification.enabled ? (
                      <span className="pill v">Real verification</span>
                    ) : (
                      <span className="pill r">Simulated — labeled demo</span>
                    )}
                  </span>
                </div>
                <div className="krow">
                  <span className="k">Method</span>
                  <span className="v">{METHODS.find((m) => m.value === data.idVerification.method)?.label}</span>
                </div>
                {data.idVerification.method === "id_document" ? (
                  <>
                    <div className="krow">
                      <span className="k">Provider</span>
                      <span className="v">{providerLabel(data.idVerification.provider)}</span>
                    </div>
                    <div className="krow">
                      <span className="k">API key</span>
                      <span className="v" style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>
                        {data.idVerification.hasKey ? data.idVerification.apiKeyMasked || "stored" : "not set"}
                      </span>
                    </div>
                    <div className="krow">
                      <span className="k">Allowed IDs</span>
                      <span className="v">
                        {data.idVerification.allowedIdTypes.length > 0
                          ? data.idVerification.allowedIdTypes
                              .map((t) => ID_TYPES.find((x) => x.value === t)?.label ?? t)
                              .join(" · ")
                          : "—"}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="krow">
                    <span className="k">Delivery</span>
                    <span className="v">Email OTP via TalentHop's own channel — no vendor</span>
                  </div>
                )}
                <div className="banner info" style={{ marginTop: 12 }}>
                  <Info />
                  <div>
                    We store only the verified <b>result</b> — never the candidate's ID number. The API key is
                    masked and never returned by the API. Changes are written to the audit log.
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Update configuration</h3>
                <span className="tag">Owner-only · audited</span>
              </div>
              <div className="card-b">
                <div
                  style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}
                >
                  <Switch on={enabled} disabled={!isOwner || save.isPending} onToggle={() => setEnabled((v) => !v)} />
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {enabled ? "Real verification enabled" : "Verification off — demo check"}
                    </div>
                    <div className="mut sm">
                      {enabled
                        ? "Candidates run a real ID + liveness check with the selected provider."
                        : "Candidates see a labeled demo check that always passes."}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div className="sm mut" style={{ marginBottom: 8 }}>
                    Verification method
                  </div>
                  {METHODS.map((m) => (
                    <label
                      key={m.value}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: `1.5px solid ${method === m.value ? "var(--teal)" : "var(--line)"}`,
                        background: method === m.value ? "var(--teal-wash)" : "transparent",
                        marginBottom: 6,
                        cursor: isOwner ? "pointer" : "default",
                      }}
                    >
                      <input
                        type="radio"
                        name="idv-method"
                        checked={method === m.value}
                        disabled={!isOwner || save.isPending}
                        onChange={() => setMethod(m.value)}
                        style={{ marginTop: 3 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13.4 }}>{m.label}</div>
                        <div className="mut sm">{m.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {method === "id_document" && (
                  <>
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label>Provider</label>
                      <select
                        value={provider}
                        onChange={(e) => setProvider(e.target.value as Provider)}
                        disabled={!isOwner || save.isPending}
                      >
                        {PROVIDERS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      <div className="hint" style={{ marginTop: 6 }}>
                        {activeCost}
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div className="sm mut" style={{ marginBottom: 8 }}>
                        Accepted government IDs
                      </div>
                      {ID_TYPES.map((t) => (
                        <div
                          key={t.value}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}
                        >
                          <span style={{ width: 150, fontWeight: 600 }}>{t.label}</span>
                          <Switch
                            on={allowedIdTypes.includes(t.value)}
                            disabled={!isOwner || save.isPending}
                            onToggle={() => toggleIdType(t.value)}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="field" style={{ marginBottom: 12 }}>
                      <label>
                        Provider API key{" "}
                        <span className="hint">write-only · stored server-side{realProvider ? "" : " · not needed for demo"}</span>
                      </label>
                      <input
                        className="input"
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={data.idVerification.hasKey ? "•••• stored — leave blank to keep" : "••••••••"}
                        disabled={!isOwner || save.isPending}
                      />
                    </div>

                    {enabled && realProvider && !data.idVerification.hasKey && !apiKey.trim() && (
                      <div className="banner warn" style={{ marginBottom: 12 }}>
                        <Alert />
                        <span>
                          A live provider needs an API key before real checks can run. The vendor call is a
                          pending integration seam until a contract is wired.
                        </span>
                      </div>
                    )}
                  </>
                )}

                {method === "otp" && (
                  <div className="banner info" style={{ marginBottom: 12 }}>
                    <Info />
                    <span>
                      No provider or API key needed — the code is sent through TalentHop's own email
                      channel to the candidate's invite address.
                    </span>
                  </div>
                )}

                {save.isError && (
                  <div className="banner crit" style={{ marginBottom: 12 }}>
                    <span>{apiErrorText(save.error, "Couldn't save provider config. Please retry.")}</span>
                  </div>
                )}
                {save.isSuccess && (
                  <div className="banner info" style={{ marginBottom: 12 }}>
                    <Info />
                    <span>Saved. The change is in the audit log.</span>
                  </div>
                )}
                <button
                  className="btn primary"
                  disabled={
                    !isOwner || (method === "id_document" && allowedIdTypes.length === 0) || save.isPending
                  }
                  onClick={() => save.mutate()}
                >
                  {save.isPending ? "Saving…" : "Save provider config"}
                </button>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Cost per check</h3>
              <span className="mut sm">Liveness COGS budget = ₹66 ({usdc(66)}) / candidate</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Per check</th>
                    <th>Fits ₹66 COGS?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 600 }}>Simulated (demo)</td>
                    <td className="tnum">₹0 · {usdc(0)}</td>
                    <td>
                      <ShieldCheck /> demo only — no real check
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>HyperVerge / IDfy</td>
                    <td className="tnum">~₹15–35 · {usdc(15)}–{usdc(35)}</td>
                    <td>
                      <span className="pill v">Yes, at the low end</span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>iProov</td>
                    <td className="tnum">~₹40–90 · {usdc(40)}–{usdc(90)}</td>
                    <td>
                      <span className="pill n">Partly — top end exceeds COGS</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="disclaimer">
        <b>Identity checks are OFF until a vendor contract is signed.</b> Until then the candidate sees a labeled
        demo check — the product never claims a real ID verification ran. The key is stored server-side, masked in
        the console and never returned by the API.
      </div>
    </section>
  );
}
